import { CheckListIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils/classnames";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { formatPhone } from "shared/utils";
import {
  BrandingFields,
  type BrandingFormValues
} from "shared/app/BrandingForm";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import * as yup from "yup";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { useAuthContext } from "providers/Auth";
import { PropertyKind } from "shared/types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { generatePrimaryPalette } from "shared/app/BrandingForm/components/ColorPicker/untils";
import { ApplyBrandingModal } from "./ApplyBrandingToProperties/ApplyBrandingModal";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

type LandlordField = "landlord_name" | "landlord_phone" | "landlord_email";

const LANDLORD_FIELDS: LandlordField[] = [
  "landlord_name",
  "landlord_phone",
  "landlord_email"
];

const SYNCED_KEY = {
  landlord_name: "landlord_name_synced",
  landlord_phone: "landlord_phone_synced",
  landlord_email: "landlord_email_synced"
} as const;

const isLandlordFieldPmsSynced = (
  pmsValues: Property["pms_values"],
  pmsOverrides: Property["pms_overrides"],
  field: LandlordField
) => Boolean(pmsValues?.[field]) && pmsOverrides?.[field] !== false;

const changedPmsOverrides = (
  values: BrandingFormValues,
  initialValues: BrandingFormValues
) => {
  const changed = LANDLORD_FIELDS.filter(
    (field) => values[SYNCED_KEY[field]] !== initialValues[SYNCED_KEY[field]]
  );

  if (changed.length === 0) return undefined;

  return Object.fromEntries(
    changed.map((field) => [field, Boolean(values[SYNCED_KEY[field]])])
  );
};

interface EditBrandingFormProps {
  onClose: () => void;
  onApplyClick: () => void;
  propertyId: number;
  showLandlordFields: boolean;
  pmsValues?: Property["pms_values"];
  initialValues: BrandingFormValues;
}

const brandingValidationSchema = yup.object({
  enabled: yup.boolean(),
  logo: yup.string().optional(),
  primary_color: yup.string().when("enabled", {
    is: true,
    then: (schema) => schema.required("The color is required"),
    otherwise: (schema) => schema.optional()
  }),
  landlord_name: yup
    .string()
    .max(100, "Must be at most 100 characters")
    .optional(),
  landlord_phone: yup
    .string()
    .test(
      "phone-length",
      "Phone number must be exactly 10 characters",
      (value) => !value || value.length === 10
    )
    .optional(),
  landlord_email: yup
    .string()
    .email("Must be a valid email address")
    .optional(),
  palette: yup.object().optional(),
  landlord_name_synced: yup.boolean(),
  landlord_phone_synced: yup.boolean(),
  landlord_email_synced: yup.boolean()
});

const BRANDING_DIRTY_KEYS = [
  "enabled",
  "logo",
  "primary_color",
  "landlord_name",
  "landlord_phone",
  "landlord_email",
  "landlord_name_synced",
  "landlord_phone_synced",
  "landlord_email_synced"
] as const;

const isBrandingDirty = (
  values: BrandingFormValues,
  initialValues: BrandingFormValues
) => BRANDING_DIRTY_KEYS.some((key) => values[key] !== initialValues[key]);

const getBrandingSwitchDescription = ({
  enabled,
  dictionary
}: {
  enabled: boolean;
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>;
}): string => {
  if (enabled)
    return `The ${dictionary.property} and all associated ${dictionary.units} will have separate branding from the company`;

  return "Branding will be set to match the company branding";
};

const EditBrandingForm = ({
  onClose,
  onApplyClick,
  propertyId,
  showLandlordFields,
  pmsValues,
  initialValues
}: EditBrandingFormProps) => {
  const queryClient = useQueryClient();
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [hasNewLogoUpload, setHasNewLogoUpload] = useState(false);
  const dictionary = useUnitsPropertiesDictionary();

  const { Property } = dictionary;

  const { register, onSubmit, values, errors } = useFormTyped(
    initialValues,
    brandingValidationSchema
  );

  const isDirty = isBrandingDirty(values, initialValues);

  const { mutate: updateBranding, isPending } = useMutation({
    mutationFn: (vals: BrandingFormValues) => {
      const pmsOverrides = changedPmsOverrides(vals, initialValues);

      return addressApi.editProperty({
        propertyId,
        ...(pmsOverrides ? { pms_overrides: pmsOverrides } : {}),
        ...(hasNewLogoUpload ? { media_session_key: sessionKey } : {}),
        branding: {
          enabled: vals.enabled,
          logo: vals.logo,
          primary_color: vals.primary_color,
          landlord_name: vals.landlord_name,
          landlord_phone: vals.landlord_phone,
          landlord_email: vals.landlord_email,
          palette: vals.palette
        }
      });
    },
    onSuccess: () => {
      toast.success("Branding updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (vals) => updateBranding(vals),
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <SectionCard
      title="Branding"
      footer={
        <div className="flex items-center justify-between w-full">
          <SaveCancelButtons
            onSave={(e) => handleSubmit(e)}
            onCancel={onClose}
            saveLoading={isPending}
            saveDisabled={
              isLogoUploading || Object.values(errors).some(Boolean)
            }
          />
          {values.enabled && (
            <Button
              HugeIcon={CheckListIcon}
              className="flex items-center gap-2 text-base font-bold"
              disabled={isDirty}
              tooltip={
                isDirty
                  ? "Save your branding changes first — only saved branding is applied to other properties"
                  : undefined
              }
              variant={ButtonVariant.TEXT}
              onClick={onApplyClick}
            >
              Apply to multiple {dictionary.properties}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="pb-6 border-b border-alpha-40 flex items-start gap-3">
          <Switch
            checked={values.enabled ?? false}
            onChange={(v) => register("enabled").onChange(v)}
            size={SwitchSize.BASE}
          />
          <div>
            <p className="text-sm font-medium text-content-700">
              {Property} level branding is{" "}
              {values.enabled ? "enabled" : "disabled"}
            </p>
            <p className="text-sm text-content-200">
              {getBrandingSwitchDescription({
                enabled: Boolean(values.enabled),
                dictionary
              })}
            </p>
          </div>
        </div>

        {values.enabled && (
          <BrandingFields
            register={register}
            sessionKey={sessionKey}
            onUploadingChange={setIsLogoUploading}
            onLogoUploaded={setHasNewLogoUpload}
            showLandlordFields={showLandlordFields}
            pmsValues={pmsValues}
          />
        )}
      </div>
    </SectionCard>
  );
};

export const BrandingSection = ({ property }: { property: Property }) => {
  const { type, partner } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const dictionary = useUnitsPropertiesDictionary();
  const { Property } = dictionary;
  const queryClient = useQueryClient();

  const showLandlordFields =
    (property.kind &&
      [PropertyKind.SingleFamily, PropertyKind.MultiFamily].includes(
        property.kind
      )) ||
    type.isPmsListingSyncEnabled;

  const partnerPrimaryColor = partner?.colors?.primary?.["500"];
  const propertyQueryKey = ["properties", String(property.id)];

  const { mutate: toggleEnabled, isPending: isToggling } = useMutation({
    mutationFn: (enabled: boolean) => {
      const needsColor = enabled && !property.branding?.primary_color;
      const defaultColor = partnerPrimaryColor || "#e6007e";
      return addressApi.editProperty({
        propertyId: property.id,
        branding: {
          enabled,
          ...(needsColor
            ? {
                primary_color: defaultColor,
                palette: generatePrimaryPalette(defaultColor)
              }
            : {})
        }
      });
    },
    onSuccess: (_, enabled) => {
      const needsColor = enabled && !property.branding?.primary_color;
      const defaultColor = partnerPrimaryColor || "#e6007e";
      const colorPatch = needsColor
        ? {
            primary_color: defaultColor,
            palette: generatePrimaryPalette(defaultColor)
          }
        : {};
      queryClient.setQueryData<Property>(propertyQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          branding: { ...(old.branding ?? {}), enabled, ...colorPatch }
        };
      });
      queryClient.invalidateQueries({ queryKey: propertyQueryKey });
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  if (isEditing)
    return (
      <>
        <EditBrandingForm
          onClose={() => setIsEditing(false)}
          onApplyClick={() => setIsApplyOpen(true)}
          propertyId={property.id}
          showLandlordFields={showLandlordFields}
          pmsValues={property.pms_values}
          initialValues={{
            enabled: property.branding?.enabled ?? false,
            logo:
              typeof property.branding?.logo === "string"
                ? property.branding.logo
                : property.branding?.logo?.asset?.url,
            primary_color:
              property.branding?.primary_color ||
              partner?.colors?.primary?.["500"],
            landlord_name: property.branding?.landlord_name,
            landlord_phone:
              property.branding?.landlord_phone?.replace(/\D/g, "") ||
              undefined,
            landlord_email: property.branding?.landlord_email,
            palette:
              property.branding?.palette ||
              (partner?.colors?.primary?.["500"]
                ? generatePrimaryPalette(partner.colors.primary["500"])
                : undefined),
            landlord_name_synced: isLandlordFieldPmsSynced(
              property.pms_values,
              property.pms_overrides,
              "landlord_name"
            ),
            landlord_phone_synced: isLandlordFieldPmsSynced(
              property.pms_values,
              property.pms_overrides,
              "landlord_phone"
            ),
            landlord_email_synced: isLandlordFieldPmsSynced(
              property.pms_values,
              property.pms_overrides,
              "landlord_email"
            )
          }}
        />
        <ApplyBrandingModal
          opened={isApplyOpen}
          onClose={() => setIsApplyOpen(false)}
          sourceProperty={property}
          showLandlordFields={showLandlordFields}
        />
      </>
    );

  const enabled = property.branding?.enabled ?? false;

  return (
    <>
      <SectionCard
        title="Branding"
        headerExtra={
          enabled ? (
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          ) : undefined
        }
        footer={
          enabled ? (
            <div className="flex items-center justify-end w-full">
              <Button
                HugeIcon={CheckListIcon}
                className="flex items-center gap-2 text-base font-bold"
                variant={ButtonVariant.TEXT}
                onClick={() => setIsApplyOpen(true)}
              >
                Apply to multiple {dictionary.properties}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className={cx(enabled && "space-y-4")}>
          <div
            className={cx(
              "flex items-start gap-3",
              enabled && "pb-4 border-b border-alpha-40"
            )}
          >
            <Switch
              checked={enabled}
              size={SwitchSize.BASE}
              disabled={isToggling}
              onChange={(v) => toggleEnabled(v)}
            />
            <div>
              <p className="text-sm font-medium text-content-700">
                {Property} level branding is {enabled ? "enabled" : "disabled"}
              </p>
              <p className="text-sm text-content-200">
                {getBrandingSwitchDescription({ enabled, dictionary })}
              </p>
            </div>
          </div>

          <Show if={enabled}>
            <div className="space-y-4">
              <Row
                title="Primary color"
                value={property.branding?.primary_color}
              />
              <Show if={showLandlordFields}>
                <Row
                  title="Landlord name"
                  value={property.branding?.landlord_name}
                />
                <Row
                  title="Landlord phone"
                  value={formatPhone(property.branding?.landlord_phone || "")}
                />
                <Row
                  title="Landlord email"
                  value={property.branding?.landlord_email}
                />
              </Show>
            </div>
          </Show>
        </div>
      </SectionCard>
      <ApplyBrandingModal
        opened={isApplyOpen}
        onClose={() => setIsApplyOpen(false)}
        sourceProperty={property}
        showLandlordFields={showLandlordFields}
      />
    </>
  );
};
